import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";
import { RecommendationModel } from "src/app/modules/user/components/group-management/travel-kit-management/recommendation-management/recommendation-form-management";
import { HeicConverterService } from "src/app/shared/services/heic-converter.service";
import { TravelKitService } from "src/app/shared/services/travel-kit.service";
import { UploadObsService } from "src/app/shared/services/upload-obs.service";
import AppShareHelper from "src/app/shared/utils/helper";
import Swal from "sweetalert2";
import { isRichTextEmpty, isRichTextTooLong } from "../shared/rich-text.utils";

@Component({
  selector: "app-tab-benefits-and-offers",
  templateUrl: "./tab-benefits-and-offers.component.html",
  styleUrls: ["./tab-benefits-and-offers.component.scss"],
})
export class TabBenefitsAndOffersComponent implements OnInit, OnChanges {
  @Input() model: any = {};
  @Input() formValid: boolean = true;
  @Input() submitted: boolean = false;
  @Input() uploadProgress: { [key: string]: number } = {};
  @Input() uploadStates: { [key: string]: boolean } = {};
  @Input() digitalLogId: number;

  // Recommendation data from parent component
  @Input() recommendations: RecommendationModel[] = [];
  @Input() allRecommendationsExpanded: boolean = false;
  @Input() recommendationChanges: boolean[] = [];
  @Input() originalRecommendations: RecommendationModel[] = [];

  @Output() modelChange = new EventEmitter<any>();
  @Output() autoCheckEvent = new EventEmitter<string>();
  @Output() recommendationsChange = new EventEmitter<RecommendationModel[]>();
  @Output() allRecommendationsExpandedChange = new EventEmitter<boolean>();
  @Output() recommendationChangesChange = new EventEmitter<boolean[]>();
  @Output() originalRecommendationsChange = new EventEmitter<RecommendationModel[]>();

  // Remove internal recommendation management (now managed by parent)
  // recommendations: RecommendationModel[] = [];
  // allRecommendationsExpanded: boolean = false;
  // recommendationChanges: boolean[] = [];
  // originalRecommendations: RecommendationModel[] = [];

  constructor(
    private toastr: ToastrService,
    private uploadService: UploadObsService,
    private travelKitService: TravelKitService,
    private _modalService: NgbModal,
    private heicConverter: HeicConverterService
  ) {}

  ngOnInit() {
    this.initializeRecommendationData();

    if (this.model.recommendations && this.model.recommendations.length > 0) {
      this.model.recommendations.forEach(section => {
        section.collapsed = false;
        if (section.subSections) {
          section.subSections.forEach(subsection => {
            subsection.collapsed = false;
          });
        }
      });
    }
    this.allRecommendationsExpanded = true;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["model"] && changes["model"].currentValue) {
      this.initializeRecommendationData();
    }
  }

  private initializeRecommendationData() {
    // If parent has recommendation data, use it; otherwise initialize from model
    if (this.recommendations && this.recommendations.length > 0) {
      // Use existing recommendation data from parent
      this.recommendationChanges = this.recommendationChanges || new Array(this.recommendations.length).fill(false);
      this.originalRecommendations = this.originalRecommendations || AppShareHelper.deepClone(this.recommendations);
    } else {
      // Initialize from model or create default recommendation
      const newRecommendations =
        this.model.recommendations && this.model.recommendations.length > 0
          ? this.model.recommendations
          : [
              {
                type: "",
                subCategory: "",
                title: "",
                description: "",
                banner: "",
                price: null,
                currency: "",
                link: "",
                digitalMemoryLogId: this.model.id || this.digitalLogId,
                collapsed: false,
              },
            ];

      // Emit changes to parent
      this.recommendationsChange.emit(newRecommendations);
      this.recommendationChangesChange.emit(new Array(newRecommendations.length).fill(false));
      this.originalRecommendationsChange.emit(AppShareHelper.deepClone(newRecommendations));
    }

    if (this.recommendations && this.recommendations.length > 0) {
      this.recommendations.forEach((recommendation: any) => {
        if (recommendation.collapsed === undefined || recommendation.collapsed === null) {
          recommendation.collapsed = false;
        }
      });
      this.recommendationsChange.emit([...this.recommendations]);
    }
  }

  autoCheck(field: string) {
    this.autoCheckEvent.emit(field);
  }

  trackByIndex(index: number, item: any): any {
    return index;
  }

  toggleExpandAllRecommendations() {
    const newExpandedState = !this.allRecommendationsExpanded;
    this.recommendations.forEach((recommendation: any) => {
      recommendation.collapsed = !newExpandedState;
    });
    this.allRecommendationsExpandedChange.emit(newExpandedState);
    this.recommendationsChange.emit([...this.recommendations]);
  }

  toggleRecommendationCollapse(recommendation: any) {
    recommendation.collapsed = !recommendation.collapsed;
    this.updateExpandAllState();
    this.recommendationsChange.emit([...this.recommendations]);
  }

  updateExpandAllState() {
    const allExpanded = this.recommendations.length > 0 && this.recommendations.every((r: any) => !r.collapsed);
    this.allRecommendationsExpandedChange.emit(allExpanded);
  }

  addRecommendationSection() {
    // Ensure arrays are properly initialized
    const currentRecommendations = Array.isArray(this.recommendations) ? this.recommendations : [];

    if (currentRecommendations.length < 6) {
      const newRecommendation: any = {
        type: "",
        subCategory: "",
        title: "",
        description: "",
        banner: "",
        price: null,
        currency: "",
        link: "",
        digitalMemoryLogId: this.model.id || this.digitalLogId,
        collapsed: false,
      };

      // Ensure arrays are properly initialized
      const currentRecommendationChanges = Array.isArray(this.recommendationChanges) ? this.recommendationChanges : [];
      const currentOriginalRecommendations = Array.isArray(this.originalRecommendations)
        ? this.originalRecommendations
        : [];

      const updatedRecommendations = [...currentRecommendations, newRecommendation];
      const updatedRecommendationChanges = [...currentRecommendationChanges, false];
      const updatedOriginalRecommendations = [
        ...currentOriginalRecommendations,
        AppShareHelper.deepClone(newRecommendation),
      ];

      // Emit changes to parent
      this.recommendationsChange.emit(updatedRecommendations);
      this.recommendationChangesChange.emit(updatedRecommendationChanges);
      this.originalRecommendationsChange.emit(updatedOriginalRecommendations);

      this.updateExpandAllState();
    } else {
      this.toastr.error("The maximum number of slots is 6");
    }
  }

  onRecommendationChange(index: number) {
    if (!Array.isArray(this.recommendationChanges) || index < 0 || index >= this.recommendationChanges.length) {
      return;
    }

    const updatedRecommendationChanges = [...this.recommendationChanges];
    updatedRecommendationChanges[index] = true;
    this.recommendationChangesChange.emit(updatedRecommendationChanges);
  }

  onRecommendationTypeChange(index: number) {
    if (this.recommendations[index] && this.recommendations[index].type !== "product") {
      (this.recommendations[index] as any).price = null;
      (this.recommendations[index] as any).currency = "";
    }

    this.onRecommendationChange(index);
    this.recommendationsChange.emit([...this.recommendations]);
  }

  private isFileSizeValid(file: File): boolean {
    const maxSize = 50 * 1024 * 1024; // 50MB
    return file.size <= maxSize;
  }

  async uploadRecommendationBanner(files: File[], recommendationIndex: number, type: string) {
    if (!files || files.length === 0) return;

    let file = files[0];

    const acceptedImageTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif"];
    const isHeicByExtension = /\.(heic|heif)$/i.test(file.name);
    const isValidType =
      acceptedImageTypes.includes(file.type.toLowerCase() || "") ||
      (isHeicByExtension && (!file.type || file.type === "application/octet-stream"));
    if (!isValidType) {
      this.toastr.error(`File ${file.name} format not supported. Accepted formats: JPEG, JPG, PNG, WEBP, HEIC`);
      return;
    }

    if (!this.isFileSizeValid(file)) {
      this.toastr.error("Max file size exceeds 50MB", "Error");
      return;
    }

    if (!this.recommendations || recommendationIndex >= this.recommendations.length || recommendationIndex < 0) {
      this.toastr.error("Invalid benefits & offers index");
      return;
    }

    file = await this.heicConverter.convertToJpegIfNeeded(file);

    const uploadKey = this.generateUploadKey(
      "recommendation_banner",
      file.name,
      `recommendation_${recommendationIndex}`
    );
    this.setUploadState(uploadKey, true);
    (this.recommendations[recommendationIndex] as any).bannerUploadKey = uploadKey;
    this.uploadService.uploadFileToS3("digital/photos", file).subscribe(
      (response: any) => {
        if (response && response.type === "progress") {
          this.setUploadProgress(uploadKey, response.percent);
          return;
        }

        if (response && response.type === "complete") {
          this.setUploadState(uploadKey, false);
          (this.recommendations[recommendationIndex] as any).banner = response.url;
          (this.recommendations[recommendationIndex] as any).bannerUploadKey = null;
          const message =
            type === "product" || type === "course"
              ? "Product Image uploaded successfully"
              : "Banner uploaded successfully";
          this.toastr.success(message);

          this.onRecommendationChange(recommendationIndex);
          this.cleanupUpload(uploadKey);
        }
      },
      error => {
        this.setUploadState(uploadKey, false);
        (this.recommendations[recommendationIndex] as any).bannerUploadKey = null;
        this.toastr.error(`Failed to upload ${file.name}: ${error.message || error}`);
        console.error("Upload error:", error);
        this.cleanupUpload(uploadKey);
      }
    );
  }

  removeRecommendationBanner(recommendationIndex: number) {
    (this.recommendations[recommendationIndex] as any).banner = "";
    this.onRecommendationChange(recommendationIndex);
  }

  deleteRecommendation(index) {
    if (!this.recommendations || index < 0 || index >= this.recommendations.length) {
      this.toastr.error("Invalid benefits & offers index");
      return;
    }

    const recommendation = this.recommendations[index];
    Swal.fire({
      title: "Confirmation",
      text: `Are you sure you want to delete this slot ${index + 1}?`,
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    }).then(result => {
      if (result.value) {
        if (recommendation.id && recommendation.id > 0) {
          // Recommendation exists in database, try to delete from API
          this.travelKitService
            .deleteRecommendation(recommendation.id)
            .toPromise()
            .then(res => {
              this.toastr.success(`Delete benefits & offers slot ${index + 1} successfully`);
              this.removeRecommendationFromArrays(index);
            })
            .catch(error => {
              console.error("Delete recommendation error:", error);
              // If delete failed, still remove from local arrays if it's a 404 (not found)
              if (error.status === 404) {
                this.toastr.warning("Recommendation was already deleted, removing from list");
                this.removeRecommendationFromArrays(index);
              }
            });
        } else {
          // Recommendation doesn't exist in database yet, just remove from local arrays
          this.removeRecommendationFromArrays(index);
          this.toastr.success(`Delete benefits & offers slot ${index + 1} successfully`);
        }
      }
    });
  }

  private removeRecommendationFromArrays(index: number) {
    const updatedRecommendations = Array.isArray(this.recommendations) ? [...this.recommendations] : [];
    const updatedRecommendationChanges = Array.isArray(this.recommendationChanges)
      ? [...this.recommendationChanges]
      : [];
    const updatedOriginalRecommendations = Array.isArray(this.originalRecommendations)
      ? [...this.originalRecommendations]
      : [];

    if (index >= 0 && index < updatedRecommendations.length) {
      updatedRecommendations.splice(index, 1);
    }
    if (index >= 0 && index < updatedRecommendationChanges.length) {
      updatedRecommendationChanges.splice(index, 1);
    }
    if (index >= 0 && index < updatedOriginalRecommendations.length) {
      updatedOriginalRecommendations.splice(index, 1);
    }

    // Always maintain at least 1 recommendation
    if (updatedRecommendations.length === 0) {
      const defaultRecommendation: any = {
        type: "",
        subCategory: "",
        title: "",
        description: "",
        banner: "",
        price: null,
        currency: "",
        link: "",
        digitalMemoryLogId: this.model.id || this.digitalLogId,
        collapsed: false,
      };
      updatedRecommendations.push(defaultRecommendation);
      updatedRecommendationChanges.push(false);
      updatedOriginalRecommendations.push(AppShareHelper.deepClone(defaultRecommendation));
    }

    // Emit changes to parent
    this.recommendationsChange.emit(updatedRecommendations);
    this.recommendationChangesChange.emit(updatedRecommendationChanges);
    this.originalRecommendationsChange.emit(updatedOriginalRecommendations);

    this.updateExpandAllState();
  }

  isValidUrl(url: string): boolean {
    if (!url) return true;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  // Helper methods from parent component
  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  generateUploadKey(prefix: string, fileName: string, suffix?: string): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    return suffix
      ? `${prefix}_${timestamp}_${random}_${suffix}_${fileName}`
      : `${prefix}_${timestamp}_${random}_${fileName}`;
  }

  setUploadState(key: string, isUploading: boolean) {
    if (!key) return;
    this.uploadStates[key] = isUploading;
  }

  setUploadProgress(key: string, progress: number) {
    if (!key) return;
    this.uploadProgress[key] = progress;
  }

  cleanupUpload(key: string) {
    if (!key) return;
    delete this.uploadProgress[key];
    delete this.uploadStates[key];
  }

  isUploadInProgress(key: string): boolean {
    if (!key) return false;
    return this.uploadStates[key] === true;
  }

  getProgressByKey(key: string): number {
    if (!key) return 0;
    return this.uploadProgress[key] || 0;
  }

  validateRecommendations(): boolean {
    if (this.model.displayRecommendations && this.recommendations.length === 0) {
      this.toastr.error("Please add Benefits & Offers");
      return false;
    }

    if (this.model.displayRecommendations && this.recommendations.length > 0) {
      for (let i = 0; i < this.recommendations.length; i++) {
        const recommendation = this.recommendations[i];

        if (!recommendation.type) {
          this.toastr.error("Please complete all required fields for Benefits and offers.");
          return false;
        }

        if (!recommendation.subCategory) {
          this.toastr.error("Please complete all required fields for Benefits and offers.");
          return false;
        }

        if (!recommendation.title) {
          this.toastr.error("Please complete all required fields for Benefits and offers.");
          return false;
        }

        if (recommendation.title && recommendation.title.length > 500) {
          this.toastr.error(`Benefits & Offers ${i + 1}: Title exceeds maximum length of 500 characters`);
          return false;
        }

        if (isRichTextEmpty(recommendation.description)) {
          this.toastr.error(`Please complete all required fields for Benefits and offers.`);
          return false;
        }

        if (!recommendation.link) {
          this.toastr.error(`Please complete all required fields for Benefits and offers.`);
          return false;
        }

        if (isRichTextTooLong(recommendation.description, 5000)) {
          this.toastr.error(`Benefits & Offers ${i + 1}: Description exceeds maximum length of 5000 characters`);
          return false;
        }

        if (!recommendation.banner) {
          this.toastr.error(`Please complete all required fields for Benefits and offers.`);
          return false;
        }

        if (recommendation.type === "product") {
          const price = (recommendation as any).price;
          const currency = (recommendation as any).currency;

          if (price === null || price === undefined || price === "") {
            this.toastr.error(`Please complete all required fields for Benefits and offers.`);
            return false;
          }

          if (price < 0) {
            this.toastr.error(`Benefits & Offers ${i + 1}: Price must be a positive number`);
            return false;
          }

          if (price.toString().length > 20) {
            this.toastr.error(`Benefits & Offers ${i + 1}: Price exceeds maximum length of 20 digits`);
            return false;
          }

          if (!currency || currency.trim() === "") {
            this.toastr.error(`Benefits & Offers ${i + 1}: Currency is required for product type`);
            return false;
          }

          if (currency.length > 100) {
            this.toastr.error(`Benefits & Offers ${i + 1}: Currency exceeds maximum length of 100 characters`);
            return false;
          }
        }

        if (recommendation.link && !this.isValidUrl(recommendation.link)) {
          this.toastr.error(`Benefits & Offers ${i + 1}: Link is not a valid URL`);
          return false;
        }
      }
    }

    return true;
  }

  saveRecommendations(): Promise<void> {
    return new Promise((resolve, reject) => {
      const sectionPromises = [];

      // Save changed recommendations
      if (this.recommendationChanges && this.recommendations) {
        // Check if we have any valid recommendations to save
        const validRecommendations = this.recommendations.filter(
          recommendation => recommendation.title && recommendation.title.trim().length > 0
        );

        // If recommendations.length = 1 and it's empty, skip all recommendation saving
        if (this.recommendations.length === 1 && validRecommendations.length === 0) {
          console.log("Skipping recommendation save: only default empty recommendation exists");
          resolve();
          return;
        }

        // Only process recommendations that have been changed AND have valid content
        this.recommendationChanges.forEach((hasChanged, index) => {
          if (hasChanged && this.recommendations[index]) {
            const recommendation = this.recommendations[index];

            // Validate required fields - skip if empty
            if (!recommendation.title || !recommendation.title.trim()) {
              console.warn(`Recommendation ${index + 1}: Title is required, skipping save`);
              return;
            }

            const recommendationData = {
              type: recommendation.type || "",
              subCategory: recommendation.subCategory || "",
              title: recommendation.title.trim(),
              description: recommendation.description || "",
              banner: recommendation.banner || "",
              price: recommendation.price || 0,
              currency: recommendation.currency || "",
              link: recommendation.link || "",
              digitalMemoryLogId: this.digitalLogId,
            };

            if (recommendation.id && recommendation.id > 0) {
              // Update existing recommendation
              sectionPromises.push(
                this.travelKitService.updateRecommendation(recommendationData, recommendation.id).toPromise()
              );
            } else {
              // Create new recommendation only if it doesn't have pending creation
              if (!recommendation.id || recommendation.id <= 0) {
                sectionPromises.push(
                  this.travelKitService
                    .createRecommendation(recommendationData)
                    .toPromise()
                    .then((response: any) => {
                      if (response && response.id) {
                        recommendation.id = response.id;
                      }
                      return response;
                    })
                );
              }
            }
          }
        });
      }

      if (sectionPromises.length > 0) {
        Promise.all(sectionPromises)
          .then(results => {
            // Reset recommendation changes after successful save
            this.recommendationChanges = new Array(this.recommendations.length).fill(false);
            this.originalRecommendations = AppShareHelper.deepClone(this.recommendations);

            resolve();
          })
          .catch(error => {
            console.error("Error processing recommendations:", error);
            this.toastr.error("Failed to save some recommendations");
            reject(error);
          });
      } else {
        resolve();
      }
    });
  }
}
